import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productosAPI, handleAPIError } from '../../services/api';

// Thunks para operaciones asíncronas
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await productosAPI.getAll(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const response = await productosAPI.create(productData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, productData }, { rejectWithValue }) => {
    try {
      const response = await productosAPI.update(id, productData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id, { rejectWithValue }) => {
    try {
      await productosAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

const initialState = {
  products: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    weight: 'all',
    length: 'all',
    boxType: 'all',
    activo: undefined
  }
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        weight: 'all',
        length: 'all',
        boxType: 'all',
        activo: undefined
      };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create product
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products.push(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update product
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.products.findIndex(product => product.id === action.payload.id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete product
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products = state.products.filter(product => product.id !== action.payload);
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  clearError
} = productsSlice.actions;

// Selectors
export const selectAllProducts = (state) => state.products.products;
export const selectProductsLoading = (state) => state.products.loading;
export const selectProductsError = (state) => state.products.error;
export const selectProductsFilters = (state) => state.products.filters;

export const selectFilteredProducts = (state) => {
  const products = selectAllProducts(state);
  const filters = selectProductsFilters(state);
  
  return products.filter(product => {
    const matchesSearch = !filters.search || 
      product.nombre?.toLowerCase().includes(filters.search.toLowerCase()) ||
      product.code?.toLowerCase().includes(filters.search.toLowerCase()) ||
      product.description?.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesWeight = filters.weight === 'all' || product.weigh_bunches_gr?.toString() === filters.weight;
    const matchesLength = filters.length === 'all' || product.large_cm?.toString() === filters.length;
    const matchesBoxType = filters.boxType === 'all' || 
      product.tipo_caja_id?.toString() === filters.boxType ||
      product.tipo_caja?.id?.toString() === filters.boxType; // Alternative path
    const matchesActivo = filters.activo === undefined || product.activo === filters.activo;
    
    return matchesSearch && matchesWeight && matchesLength && matchesBoxType && matchesActivo;
  });
};

// Selector para productos activos (usado en pedidos)
export const selectActiveProducts = (state) => {
  const products = selectAllProducts(state);
  return products.filter(product => product.activo !== false);
};

export const selectProductById = (state, productId) => {
  return state.products.products.find(product => product.id === productId);
};

export default productsSlice.reducer;
